import React from 'react';
import Icon from './Icon';

/**
 * Cockpit StatCard — mono eyebrow, big tight figure, delta chip.
 * Lifts on hover; the delta arrow points in its direction.
 */
export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  delta?: string;
  deltaTone?: 'up' | 'down' | 'warn' | 'idle';
  /** Which way the arrow points, when it differs from the tone (a rising cost is bad news). Defaults to the tone. */
  deltaDirection?: 'up' | 'down';
  hint?: React.ReactNode;
  style?: React.CSSProperties;
}

export default function StatCard({ label, value, delta, deltaTone, deltaDirection, hint, style }: StatCardProps) {
  const up = typeof delta === 'string' && delta.trim().startsWith('+');
  const tone = deltaTone || (up ? 'up' : 'down');
  const toneColor = {
    up: 'var(--status-up)',
    down: 'var(--status-down)',
    warn: 'var(--status-warn)',
    idle: 'var(--status-idle)',
  }[tone];
  return (
    <div
      className="ck-statcard hover-lift"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        padding: 'var(--space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        minWidth: 0,
        ...style,
      }}
    >
      <span className="t-eyebrow">{label}</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <span
          className="t-num ck-statcard-value"
          style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            lineHeight: 'var(--leading-tight)',
            color: 'var(--text-body)',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
        {delta ? (
          <span
            className="t-num"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: toneColor,
            }}
          >
            <Icon name={(deltaDirection ?? (tone === 'down' ? 'down' : 'up')) === 'down' ? 'arrow-down-right' : 'arrow-up-right'} size={13} strokeWidth={2.25} />
            {delta.replace(/^[+-]\s*/, '')}
          </span>
        ) : null}
      </div>
      {hint ? <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{hint}</span> : null}
    </div>
  );
}
